/**
 * SIMS-8607 — member-ID pattern validity, sample testing and generated-ID preview.
 *
 * Pure functions, deliberately. Two of this story's TestRail cases are compliance-shaped and both
 * are satisfied by that purity rather than by discipline:
 *
 *   C17572 — the tester's SAMPLE is never persisted or logged. An administrator will realistically
 *            paste a real member ID into the tester, and member IDs are participant identifiers
 *            from a clinical context (the same concern C-4 raises for SIMS-8622). The sample is an
 *            argument here and the return value is a verdict: there is no module state that could
 *            hold, cache or later transmit it, and nothing here logs.
 *   C17575 — the PREVIEW must not consume or reserve a real member ID. `generateSampleId` is a pure
 *            seeded generator: no request, no sequence, nothing allocated. Its determinism is also
 *            what lets the preview be asserted.
 *
 * Behaviour ported from the design's own implementation (`Payers Pattern v2.dc.html`):
 * `patternValid`/`testPattern` L674-680, `genId` L683-720.
 */

/** Longest pattern we will attempt to expand. Anything longer is refused rather than hung on. */
const MAX_PATTERN_LENGTH = 200;
/** Iteration guard when expanding a pattern (the design uses the same bound). */
const MAX_EXPANSION_STEPS = 200;

export type SampleTestState = "idle" | "match" | "no-match" | "invalid";

export interface SampleTestResult {
	state: SampleTestState;
	/** Always populated for a non-idle state: hand-off §6 requires TEXT, not a colour swap. */
	label: string;
}

/**
 * Catastrophic-backtracking shapes: nested quantifiers over a quantified group, e.g. `(a+)+`.
 * Such a pattern would not reject a member ID at registration — it would hang the request, which
 * is worse. Cheap structural check; not a general ReDoS analysis.
 */
const NESTED_QUANTIFIER = /\([^()]*[+*][^()]*\)\s*[+*]/;

/**
 * Whether a pattern is usable as a member-ID rule (AC4).
 *
 * Rejects: unparseable regexes, empty patterns, patterns nothing can match, and the nested-
 * quantifier shapes above.
 */
export function isPatternUsable(pattern: string): boolean {
	const trimmed = pattern.trim();
	if (trimmed === "" || trimmed.length > MAX_PATTERN_LENGTH) {
		return false;
	}
	if (NESTED_QUANTIFIER.test(trimmed)) {
		return false;
	}
	let compiled: RegExp;
	try {
		compiled = new RegExp(trimmed);
	} catch {
		return false;
	}
	// A pattern that matches nothing at all — `(?!)` and friends — would refuse every member ID.
	// Probing the empty string plus one representative value is enough to catch the degenerate
	// case without pretending to decide emptiness in general.
	if (!compiled.test("") && !/[A-Za-z0-9\\[\](){}.+*?]/.test(trimmed)) {
		return false;
	}
	if (/\(\?!\)/.test(trimmed)) {
		return false;
	}
	return true;
}

/** Anchor a pattern so a partial match is not reported as a match. */
const anchored = (pattern: string): RegExp => {
	const body = pattern.trim().replace(/^\^/, "").replace(/\$$/, "");
	return new RegExp(`^(?:${body})$`);
};

/**
 * Test one sample against a pattern (AC2).
 *
 * An invalid pattern reports `invalid`, never `no-match` — telling the user "No match" when the
 * PATTERN is broken sends them off to fix the sample instead.
 */
export function testSampleAgainstPattern(
	pattern: string,
	sample: string,
): SampleTestResult {
	if (!isPatternUsable(pattern)) {
		return { state: "invalid", label: "Pattern is not valid" };
	}
	if (sample.trim() === "") {
		return { state: "idle", label: "" };
	}
	try {
		return anchored(pattern).test(sample)
			? { state: "match", label: "Match" }
			: { state: "no-match", label: "No match" };
	} catch {
		return { state: "invalid", label: "Pattern is not valid" };
	}
}

const DIGITS = "0123456789";
// Ambiguous glyphs (I, O) omitted, as in the design's set: a previewed ID is read aloud and typed.
const UPPER = "ABCDEFGHJKLMNPQRSTUVWXYZ";
const LOWER = "abcdefghijkmnpqrstuvwxyz";

/**
 * Generate one example ID a pattern would produce (AC3).
 *
 * Seeded LCG, ported from the design (L683-720): identical seed gives an identical ID, so
 * "Generate another" is just the next seed. Pure and offline — nothing is reserved (C17575).
 *
 * Returns "—" for a pattern it cannot expand, rather than throwing into the editor.
 */
export function generateSampleId(pattern: string, seed: number): string {
	if (!isPatternUsable(pattern)) {
		return "—";
	}
	const src = pattern.trim().replace(/^\^/, "").replace(/\$$/, "");

	let state = (seed * 9301 + 49297) % 233280;
	const next = (n: number): number => {
		state = (state * 9301 + 49297) % 233280;
		return Math.floor((state / 233280) * n);
	};
	const pick = (set: string, count: number): string => {
		let out = "";
		for (let k = 0; k < count; k += 1) {
			out += set[next(set.length)];
		}
		return out;
	};

	let out = "";
	let i = 0;
	let steps = 0;
	while (i < src.length && steps < MAX_EXPANSION_STEPS) {
		steps += 1;
		let set: string | null = null;
		const ch = src[i];

		if (ch === "\\" && src[i + 1] === "d") {
			set = DIGITS;
			i += 2;
		} else if (ch === "\\" && src[i + 1] === "w") {
			set = DIGITS + UPPER;
			i += 2;
		} else if (ch === "[") {
			const end = src.indexOf("]", i);
			if (end === -1) {
				return "—";
			}
			const body = src.slice(i + 1, end);
			set = "";
			if (/0-9/.test(body) || /\\d/.test(body)) set += DIGITS;
			if (/A-Z/.test(body)) set += UPPER;
			if (/a-z/.test(body)) set += LOWER;
			if (set === "") set = body.replace(/[\\^-]/g, "") || DIGITS;
			i = end + 1;
		} else if (ch === "\\") {
			out += src[i + 1] ?? "";
			i += 2;
			continue;
		} else {
			// A literal — the design keeps prefixes such as "MT" verbatim.
			out += ch;
			i += 1;
			continue;
		}

		let count = 1;
		const rest = src.slice(i);
		const quantifier = rest.match(/^\{(\d+)(,(\d*))?\}/);
		if (quantifier) {
			const lo = parseInt(quantifier[1], 10);
			const hi = quantifier[3]
				? parseInt(quantifier[3], 10)
				: quantifier[2]
					? lo + 2
					: lo;
			count = lo + next(Math.max(1, hi - lo + 1));
			i += quantifier[0].length;
		} else if (rest[0] === "+") {
			count = 4 + next(3);
			i += 1;
		} else if (rest[0] === "*") {
			count = next(4);
			i += 1;
		} else if (rest[0] === "?") {
			count = next(2);
			i += 1;
		}
		out += pick(set, count);
	}

	return out === "" ? "—" : out;
}
